// Shared host-bridge data client (React + TS port of v1 ParentDataHandler.js).
//
// The app runs embedded in an iframe inside the parent SCC shell and communicates with it via
// window.parent.postMessage() with GUID-correlated replies — NEVER direct fetch/XHR. This is
// the single shared transport every screen's data layer builds on (react-web profile:
// dataBridge.boundaryKind = host_bridge).

// Import DarkMode from its leaf module (NOT from ../util/RouteConstants) to
// avoid the circular dependency
// ParentDataHandler -> RouteConstants -> Associate -> ParentDataHandler.
import { DarkMode } from '../util/DarkMode';

type Resolver = (value: unknown) => void;

interface OutboundMessage {
  method: string;
  args: Record<string, unknown>;
  guid?: string;
}

export interface ApiGatewayRequest {
  resourcePath: string;
  httpMethod: string;
  processName: string;
  requestBody: Record<string, unknown>;
}

export interface ApiGatewayResponse<T = unknown> {
  status: number;
  data: T;
}

export const TIMEOUT_STRING = 'TIMEOUT';

const pending: Record<string, { resolve: Resolver }> = {};

function generateGuid(): string {
  const s4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return `${s4()}${s4()}-${s4()}-4${s4().substr(0, 3)}-${s4()}-${s4()}${s4()}${s4()}`.toLowerCase();
}

function postMessagePromised<T>(message: OutboundMessage, timeout?: number): Promise<T> {
  const guid = generateGuid();
  message.guid = guid;
  return new Promise<T>((resolve) => {
    pending[guid] = { resolve: resolve as Resolver };
    if (typeof window === 'undefined' || !window.parent) {
      resolve(undefined as unknown as T);
      return;
    }
    window.parent.postMessage(message, '*');
    if (timeout) {
      setTimeout(() => pending[guid]?.resolve(TIMEOUT_STRING as unknown as T), timeout);
    }
  });
}

/** Station code from the iframe URL query param (?stationCode=XXXX). */
export function getStationCode(): string {
  return new URLSearchParams(window.location.search).get('stationCode') ?? '';
}

/** Proxied API Gateway call through the parent shell. */
export function getAPIGatewayDataFromParent<T = unknown>(args: ApiGatewayRequest): Promise<ApiGatewayResponse<T>> {
  return postMessagePromised<ApiGatewayResponse<T>>({
    method: 'getAPIGatewayDataFromParent',
    args: args as unknown as Record<string, unknown>,
  });
}

/** The current document URL (used to build cross-app deep-links). */
export function getDocumentURL(): Promise<string> {
  return postMessagePromised<string>({ method: 'getDocumentURL', args: {} });
}

/** Feature-flag values from the parent shell. */
export function getFeaturesFromParent<T = Record<string, boolean>>(features: string[]): Promise<{ data: T }> {
  return postMessagePromised<{ data: T }>({
    method: 'getFeatureStatus',
    args: { features, stationCode: getStationCode() },
  });
}

/**
 * Ask the parent shell for the user's locale (v1 ParentDataHandler.getLocaleFromParent parity).
 * The response payload is `{ locale: string }` where `locale` is a JSON-stringified BCP-47 tag
 * (e.g. `"\"en-US\""`) — LocaleHandler unwraps that. Returns TIMEOUT_STRING if the parent
 * doesn't reply within 200ms (v1 uses navigator.language as the fallback).
 */
export function getLocaleFromParent(): Promise<{ locale: string } | typeof TIMEOUT_STRING> {
  return postMessagePromised<{ locale: string } | typeof TIMEOUT_STRING>(
    { method: 'getLocale', args: {} },
    200,
  );
}

/** Fire-and-forget client-side metrics (station-scoped variant included, matching v1). */
export function publishClientSideMetrics(args: Record<string, unknown>): void {
  window.parent.postMessage({ method: 'publishClientSideMetrics', args }, '*');
  const stationArgs = { ...args, processName: `${args.processName}_${getStationCode()}` };
  window.parent.postMessage({ method: 'publishClientSideMetrics', args: stationArgs }, '*');
}

/** Publish a SCC_INDUCT route-path count metric (v1 app.js publishCountToCSM parity). */
export function publishRoutePath(pathname: string): void {
  if (typeof window === 'undefined' || !window.parent) return;
  publishClientSideMetrics({
    processName: 'SCC_INDUCT',
    metricName: pathname,
    metricValue: 1,
    metricType: 'count',
  });
}

// Dark-mode seam (v1 ParentDataHandler getDarkModeStatus/setDarkModeStateListener parity).
// The host reply is a broadcast (NOT guid-correlated), so it is routed separately in the
// message listener below and fanned out to every registered dark-mode listener.
const darkModeListeners = new Set<(mode: DarkMode) => void>();

/**
 * Register a dark-mode listener. Returns an unsubscribe function that removes exactly
 * this listener. Listeners only fire after the host replies (see sendDarkModeStatus below).
 */
export function subscribeDarkMode(listener: (mode: DarkMode) => void): () => void {
  darkModeListeners.add(listener);
  return () => {
    darkModeListeners.delete(listener);
  };
}

/** Fire-and-forget request asking the host for the current dark-mode status. */
export function getDarkModeStatus(): void {
  if (typeof window === 'undefined' || !window.parent) return;
  window.parent.postMessage({ method: 'getDarkModeStatus', args: {} }, '*');
}

// Response router — resolves the pending promise whose guid matches the parent's reply.
const RESPONSE_HANDLERS: Record<string, 'responseData' | 'childData'> = {
  sendApiGatewayResponse: 'responseData',
  sendDocumentURL: 'responseData',
  sendFeatureStatus: 'responseData',
  // v1 ParentDataHandler.js:130 — locale replies land as `childData`, not `responseData`.
  sendLocaleResponse: 'childData',
};

if (typeof window !== 'undefined') {
  window.addEventListener(
    'message',
    (event: MessageEvent) => {
      const data = event?.data as {
        method?: string;
        guid?: string;
        responseData?: unknown;
        childData?: { capability?: unknown };
      };
      if (!data || !data.method) return;

      // Dark-mode broadcast: host-initiated reply, NOT guid-correlated. v1 read the value from
      // childData.capability ('dark' | 'light'); map DARK iff that string is DarkMode.DARK.
      if (data.method === 'sendDarkModeStatus') {
        const mode = data.childData?.capability === DarkMode.DARK ? DarkMode.DARK : DarkMode.LIGHT;
        darkModeListeners.forEach((listener) => listener(mode));
        return;
      }

      const key = RESPONSE_HANDLERS[data.method];
      if (key && data.guid && pending[data.guid]) {
        pending[data.guid].resolve(data[key]);
        delete pending[data.guid];
      }
    },
    false,
  );
}
